import { ArrowRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton, ArrowLink } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { MembraneDiagram, StrataDiagram } from "@/components/site/Diagrams";

const GUARANTEES = [
  { t: "Role-aware memory", d: "Retrieval respects who is asking. Restricted material is refused or redacted at plan time, before anything is assembled." },
  { t: "The Smritium Membrane", d: "PII detection and redaction, human approval, and a tamper-evident egress audit on any crossing to a cloud model." },
  { t: "Append-only audit trails", d: "Every retrieval and every egress is recorded immutably — evidence you can hand an auditor, not assemble by hand." },
  { t: "Compliance by architecture", d: "DPDP Act 2023 and GDPR posture become properties of the system: residency, provenance and audit, generated not asserted." },
];

export default function UEII() {
  return (
    <div data-testid="ueii-page">
      <Seo title="UEII — Unified Enterprise Intelligence Infrastructure" description="UPII's guarantees at organizational scale: role-aware memory, the Smritium Membrane, and compliance by architecture." />
      <PageHero
        eyebrow="Research · UEII"
        title="Unified Enterprise Intelligence Infrastructure"
        lead="UPII's guarantees, engineered for organizations. The same local-first substrate — plus governance, PII membranes, and tamper-evident audit — so institutional knowledge compounds inside your walls."
      >
        <CTAButton to="/company/contact" variant="solid">
          Become a design partner <ArrowRight className="h-4 w-4" />
        </CTAButton>
      </PageHero>

      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Category creation</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-[1.08] tracking-tighter text-bone md:text-4xl">
              Enterprises don't need another copilot. They need an intelligence infrastructure they can subpoena-proof.
            </h2>
            <p className="mt-6 text-[15px] leading-relaxed text-mist">
              Your institutional knowledge is your moat. It should compound inside your walls — not inside a vendor's
              model, meter, or terms of service you don't control.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <StrataDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 1 — The memory substrate beneath models and applications.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <Eyebrow>What UEII adds</Eyebrow>
          <h2 className="mt-5 font-serif text-3xl tracking-tighter text-bone md:text-5xl">Guarantees at organizational scale.</h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
          {GUARANTEES.map((g) => (
            <Item key={g.t} className="bg-ink p-8 md:p-10">
              <h3 className="font-serif text-xl text-bone">{g.t}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-mist">{g.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>The Membrane</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              The compliance layer your regulators have been asking for.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              When something truly must cross to a cloud model, the Membrane detects and redacts PII, shows exactly what
              is about to leave, requires human approval, and writes a tamper-evident egress audit log. Provable data
              sovereignty — not a policy PDF.
            </p>
            <div className="mt-8">
              <ArrowLink to="/blog/from-upii-to-ueii">Read DF-006 — From UPII to UEII</ArrowLink>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <MembraneDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 2 — The Membrane governs every crossing.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-2xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            Deploy the substrate inside your walls.
          </h2>
          <CTAButton to="/company/contact" variant="outline">
            Talk to us about UEII <ArrowRight className="h-4 w-4" />
          </CTAButton>
        </Reveal>
      </Section>
    </div>
  );
}
